import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationOnIcon,
  CalendarToday as CalendarTodayIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { format, parseISO, differenceInDays } from 'date-fns';
import { useTranslation } from 'react-i18next';
import FlightScheduleSummary from './FlightScheduleSummary';
import { getEnrichedJourneyLegs } from '../utils/flightJourneyUtils';
import { getFlightLegRouteLabel } from '../utils/flightRouteUtils';

const TRANSPORT_DATA_SKIP_KEYS = new Set(['all_items', 'all_flights', 'road_trip_data']);

const FLIGHT_LEG_DERIVED_KEYS = new Set([
  'legs',
  'airline',
  'flight_number',
  'arrival_time',
  'departure_time',
  'arrival_airport',
  'departure_airport',
  'terminal',
]);

const ItemDetailModal = ({ 
  open, 
  onClose, 
  item, 
  canEdit = false,
  onEdit,
  onDelete,
  onMoveToPersonal,
  onMoveToShared,
  getCategoryColor,
  getCategoryIcon,
  getCurrencySymbol,
  generateGoogleMapsLink
}) => {
  const { t } = useTranslation();

  if (!item) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            background: getCategoryColor ? getCategoryColor(item.category) : 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            flexShrink: 0
          }}>
            {getCategoryIcon ? getCategoryIcon(item.category, item.transportation_type) : <EventIcon />}
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              {item.name || 'Itinerary Item'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              {item.category?.charAt(0).toUpperCase() + item.category?.slice(1) || 'Activity'}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Location */}
          {item.location && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#374151' }}>
                {t('itinerary.detailModal.location')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOnIcon sx={{ fontSize: 18, color: '#64748b' }} />
                <Typography 
                  variant="body1" 
                  component="a"
                  href={generateGoogleMapsLink ? generateGoogleMapsLink(item.location) : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.location)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  sx={{ 
                    color: '#3b82f6',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    '&:hover': {
                      color: '#1d4ed8'
                    }
                  }}
                >
                  {item.location}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Date and Time */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#374151' }}>
              {t('itinerary.detailModal.dateTime')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CalendarTodayIcon sx={{ fontSize: 18, color: '#64748b' }} />
              <Typography variant="body1" sx={{ color: '#374151' }}>
                {item.start_datetime && item.end_datetime ? 
                  `${format(parseISO(item.start_datetime), 'MMM d, yyyy')} - ${format(parseISO(item.end_datetime), 'MMM d, yyyy')}` :
                  item.start_datetime ? 
                  format(parseISO(item.start_datetime), 'MMM d, yyyy') :
                  t('itinerary.detailModal.dateNotSpecified')
                }
              </Typography>
            </Box>
            {item.start_datetime && item.end_datetime && (
              <Typography variant="body2" sx={{ color: '#64748b', ml: 4 }}>
                {t('itinerary.duration')}: {t('itinerary.day', { count: differenceInDays(parseISO(item.end_datetime), parseISO(item.start_datetime)) + 1 })}
              </Typography>
            )}
          </Box>

          {/* Transportation Details */}
          {(() => {
            // Parse transportation_data if it's a string
            let transportData = item.transportation_data;
            if (typeof transportData === 'string') {
              try {
                transportData = JSON.parse(transportData);
              } catch (e) {
                transportData = null;
              }
            }
            
            if (transportData && Object.keys(transportData).length > 0) {
              const flightLegs = Array.isArray(transportData.legs) ? transportData.legs : [];
              const hasFlightLegs = flightLegs.length > 0;
              const enrichedLegs = hasFlightLegs
                ? getEnrichedJourneyLegs({ legs: flightLegs }, item)
                : [];
              const terminal = transportData.terminal;

              return (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#374151' }}>
                    {t('itinerary.detailModal.details')}
                  </Typography>

                  {hasFlightLegs && enrichedLegs.map((leg, legIndex) => {
                    const legRoute = getFlightLegRouteLabel(
                      flightLegs[legIndex],
                      legIndex,
                      flightLegs
                    );
                    const legFlightNumber = leg.airline && leg.flight_number
                      ? `${leg.airline}${leg.flight_number}`.toUpperCase()
                      : flightLegs[legIndex].flight_number?.toUpperCase();

                    return (
                      <Box key={`detail-leg-${legIndex}`} sx={{ mb: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            color: '#64748b',
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            mb: 0.75,
                          }}
                        >
                          {t('itinerary.flightLegs.legNumber', { number: legIndex + 1 })}
                          {legRoute ? ` · ${legRoute}` : ''}
                          {legFlightNumber ? ` · ${legFlightNumber}` : ''}
                        </Typography>
                        <FlightScheduleSummary
                          departureTime={leg.departure_time}
                          arrivalTime={leg.arrival_time}
                          originCode={leg.departure_airport}
                          destinationCode={leg.arrival_airport}
                          terminal={legIndex === 0 ? terminal : null}
                        />
                      </Box>
                    );
                  })}

                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
                    gap: 1.5 
                  }}>
                    {Object.entries(transportData).map(([key, value]) => {
                      if (!value || TRANSPORT_DATA_SKIP_KEYS.has(key)) return null;
                      if (hasFlightLegs && FLIGHT_LEG_DERIVED_KEYS.has(key)) return null;
                      if (typeof value === 'object') return null;
                      
                      // Create a mapping of possible field name formats to translation keys
                      const fieldNameMap = {
                        'airline': 'airline',
                        'Airline': 'airline',
                        'flight_number': 'flightNumber',
                        'Flight Number': 'flightNumber',
                        'arrival_time': 'arrivalTime',
                        'Arrival Time': 'arrivalTime',
                        'departure_time': 'departureTime',
                        'Departure Time': 'departureTime',
                        'arrival_airport': 'arrivalAirport',
                        'Arrival Airport': 'arrivalAirport',
                        'departure_airport': 'departureAirport',
                        'Departure Airport': 'departureAirport',
                        'terminal': 'terminal',
                        'Terminal': 'terminal',
                        'gate': 'gate',
                        'Gate': 'gate',
                        'seat': 'seat',
                        'Seat': 'seat',
                        'confirmation_number': 'confirmationNumber',
                        'Confirmation Number': 'confirmationNumber',
                        'booking_reference': 'bookingReference',
                        'Booking Reference': 'bookingReference',
                        'pickup_time': 'pickupTime',
                        'Pickup Time': 'pickupTime',
                        'return_time': 'returnTime',
                        'Return Time': 'returnTime',
                        'pickup_location': 'pickupLocation',
                        'Pickup Location': 'pickupLocation',
                        'dropoff_location': 'dropoffLocation',
                        'Dropoff Location': 'dropoffLocation'
                      };
                      
                      // Get the translation key from the map, or convert snake_case to camelCase as fallback
                      const mappedKey = fieldNameMap[key] || key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
                      const translationKey = `itinerary.transportFields.${mappedKey}`;
                      const hasTranslation = t(translationKey) !== translationKey;
                      const displayKey = hasTranslation ? t(translationKey) : key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                      
                      return (
                        <Box key={key} sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#64748b', fontSize: '0.8rem' }}>
                            {displayKey}
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#374151' }}>
                            {value}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            }
            return null;
          })()}

          {/* Description */}
          {item.description && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#374151' }}>
                {t('itinerary.detailModal.description')}
              </Typography>
              <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6 }}>
                {item.description}
              </Typography>
            </Box>
          )}

          {/* Cost */}
          {item.cost && parseFloat(item.cost) > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#374151' }}>
                {t('itinerary.detailModal.cost')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ color: '#059669', fontWeight: 600 }}>
                  {(() => {
                    const currency = item.cost_currency || 'USD';
                    const symbol = getCurrencySymbol ? getCurrencySymbol(currency) : '$';
                    return `${symbol}${parseFloat(item.cost).toFixed(2)}`;
                  })()}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  {t('itinerary.estimatedCost')}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Google Maps Link */}
          {item.google_map_link && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#374151' }}>
                {t('itinerary.detailModal.googleMaps')}
              </Typography>
              <Typography 
                variant="body1" 
                component="a"
                href={item.google_map_link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                sx={{ 
                  color: '#3b82f6',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  '&:hover': {
                    color: '#1d4ed8'
                  }
                }}
              >
                {t('itinerary.detailModal.viewOnGoogleMaps')}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t('itinerary.detailModal.close')}
        </Button>
        {onMoveToPersonal && (
          <Button
            onClick={() => {
              onClose();
              onMoveToPersonal();
            }}
            color="primary"
            data-testid="item-detail-move-personal"
          >
            {t('itinerary.detailModal.moveToPersonal')}
          </Button>
        )}
        {onMoveToShared && (
          <Button
            onClick={() => {
              onClose();
              onMoveToShared();
            }}
            color="primary"
            data-testid="item-detail-move-shared"
          >
            {t('itinerary.detailModal.moveToShared')}
          </Button>
        )}
        {canEdit && onDelete && (
          <Button
            onClick={() => {
              onClose();
              onDelete();
            }}
            color="error"
            startIcon={<DeleteIcon />}
            data-testid="item-detail-remove"
          >
            {t('itinerary.detailModal.remove')}
          </Button>
        )}
        {canEdit && (
          <Button 
            onClick={() => {
              onClose();
              onEdit && onEdit();
            }} 
            variant="contained" 
            color="primary"
            startIcon={<EditIcon />}
          >
            {t('itinerary.detailModal.edit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ItemDetailModal;

